import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { ref as dbRef, onValue } from "firebase/database";
import { firestore } from "../../../helpers/firebaseConfig";
import { cloudRunStartGeneralModel } from "../../../helpers/AI_functions";
import { trackBuilderEvent, trackBuilderFailure } from "../../../helpers/builderAnalytics";
import {
  normalizeRun,
  formatStatus,
  mapRoutingForApi,
  ROUTING_OPTIONS,
  toNumberOrFallback,
  defaultRunName,
} from "../../../helpers/modelBuilderHelpers";
import { LAYER_FILE, parseLayerDoc } from "../../../helpers/modelBuilder/mbChatPureUtils";
import HazardSelect from "./HazardSelect";
import "./MBRunTab.css";

const PAGE_SIZE = 8;
const CRITICAL_FACILITIES_OPTIONS = ["min", "max"];
const RUN_LAYER_TYPES = ["links", "nodes", "parcels"];
const DEFAULT_DESTINATION_CONFIG = "destinations";
const DEFAULT_DEPARTURE_TIMES_CONFIG = "departure_times";
const DEFAULT_CRITICAL_FACILITIES = "min";

export default function MBRunTab({ simulationId, database, showHazards = true }) {
  const knownRunStatuses = useRef(null);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState("");
  const [busyRunId, setBusyRunId] = useState("");
  const [editingRunId, setEditingRunId] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [confirmRun, setConfirmRun] = useState(null);
  const [logsRun, setLogsRun] = useState(null);
  const [logLines, setLogLines] = useState([]);
  const [fieldDrafts, setFieldDrafts] = useState({});
  const [gisLayers, setGisLayers] = useState([]);
  const [hazardLayers, setHazardLayers] = useState([]);
  const [hazardLayersLoaded, setHazardLayersLoaded] = useState(false);
  const [destinationConfigOptions, setDestinationConfigOptions] = useState([
    DEFAULT_DESTINATION_CONFIG,
  ]);
  const [departureTimesConfigOptions, setDepartureTimesConfigOptions] = useState([
    DEFAULT_DEPARTURE_TIMES_CONFIG,
  ]);
  const [hazardConfigOptions, setHazardConfigOptions] = useState([]);
  const [contraflowConfigOptions, setContraflowConfigOptions] = useState([]);
  const [blockedRoadConfigOptions, setBlockedRoadConfigOptions] = useState([]);
  const [page, setPage] = useState(1);

  const runsColRef = useMemo(() => {
    if (!simulationId) return null;
    return collection(firestore, "model_builders", String(simulationId), "runs");
  }, [simulationId]);

  const gisLayersColRef = useMemo(() => {
    if (!simulationId) return null;
    return collection(firestore, "model_builders", String(simulationId), "gis_layers");
  }, [simulationId]);

  const customConfigColRef = useMemo(() => {
    if (!simulationId) return null;
    return collection(firestore, "model_builders", String(simulationId), "custom_config");
  }, [simulationId]);

  const hazardLayersColRef = useMemo(() => {
    if (!simulationId) return null;
    return collection(firestore, "model_builders", String(simulationId), "hazard_layers");
  }, [simulationId]);

  useEffect(() => {
    let cancelled = false;
    if (!runsColRef) {
      setRuns([]);
      setLoading(false);
      return undefined;
    }

    const unsub = onSnapshot(
      runsColRef,
      (snap) => {
        if (cancelled) return;
        const next = snap.docs.map(normalizeRun);
        next.sort((a, b) => b.createdAtMs - a.createdAtMs || a.name.localeCompare(b.name));
        const previous = knownRunStatuses.current;
        if (previous) next.forEach((run) => {
          const before = previous.get(run.id);
          if (before && before !== run.status && ["done", "complete", "completed", "failed", "error"].includes(run.status)) {
            trackBuilderEvent(["done", "complete", "completed"].includes(run.status) ? "builder_run_completed" : "builder_run_failed", { builder_id: simulationId, tab: "run", run_id: run.id, status: run.status });
          }
        });
        knownRunStatuses.current = new Map(next.map((run) => [run.id, run.status]));
        setRuns(next);
        setLoading(false);
      },
      (err) => {
        trackBuilderFailure("builder_run_status_load_failed", { builder_id: simulationId, tab: "run", endpoint: "runs_snapshot" }, err);
        if (!cancelled) {
          setSaveError(String(err?.message || err));
          setLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
      unsub();
    };
  }, [runsColRef]);

  useEffect(() => {
    if (!gisLayersColRef) {
      setGisLayers([]);
      return undefined;
    }

    const unsub = onSnapshot(
      gisLayersColRef,
      (snap) => {
        const next = snap.docs
          .map((d) => parseLayerDoc(d.id, d.data()))
          .filter((row) => row && RUN_LAYER_TYPES.includes(row.type))
          .sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setGisLayers(next);
      },
      (err) => {
        console.warn("Failed to load run layer options:", err);
      }
    );

    return () => unsub();
  }, [gisLayersColRef]);

  useEffect(() => {
    if (!customConfigColRef) {
      setDestinationConfigOptions([DEFAULT_DESTINATION_CONFIG]);
      setDepartureTimesConfigOptions([DEFAULT_DEPARTURE_TIMES_CONFIG]);
      setHazardConfigOptions([]);
      setContraflowConfigOptions([]);
      setBlockedRoadConfigOptions([]);
      return undefined;
    }

    const unsub = onSnapshot(
      customConfigColRef,
      (snap) => {
        const destinationNames = [];
        const departureTimesNames = [];
        const hazardConfigNames = [];
        const contraflowConfigs = [];
        const blockedRoadConfigs = [];

        snap.docs.forEach((d) => {
          const id = String(d.id || "").trim();
          if (!id) return;
          const data = d.data() || {};
          const type = String(data.type || "destination").toLowerCase();
          if (type === "destination") destinationNames.push(id);
          if (type === "start_times") departureTimesNames.push(id);
          if (type === "hazard_layer") hazardConfigNames.push(id);
          if (type === "blocked_road") blockedRoadConfigs.push({ name: id, linksLayerId: String(data.linksLayerId || data.links_layer_id || "") });
          if (type === "contraflow") {
            contraflowConfigs.push({
              name: id,
              linksLayerId: String(data.linksLayerId || data.links_layer_id || ""),
            });
          }
        });

        destinationNames.sort((a, b) => a.localeCompare(b));
        departureTimesNames.sort((a, b) => a.localeCompare(b));

        const nextDestination = [DEFAULT_DESTINATION_CONFIG];
        destinationNames.forEach((name) => {
          if (!nextDestination.includes(name)) nextDestination.push(name);
        });
        setDestinationConfigOptions(nextDestination);

        const nextDepartureTimes = [DEFAULT_DEPARTURE_TIMES_CONFIG];
        departureTimesNames.forEach((name) => {
          if (!nextDepartureTimes.includes(name)) nextDepartureTimes.push(name);
        });
        setDepartureTimesConfigOptions(nextDepartureTimes);
        hazardConfigNames.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
        setHazardConfigOptions(hazardConfigNames);
        contraflowConfigs.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
        setContraflowConfigOptions(contraflowConfigs);
        blockedRoadConfigs.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
        setBlockedRoadConfigOptions(blockedRoadConfigs);
      },
      (err) => {
        console.warn("Failed to load destination config options:", err);
        setDestinationConfigOptions([DEFAULT_DESTINATION_CONFIG]);
        setDepartureTimesConfigOptions([DEFAULT_DEPARTURE_TIMES_CONFIG]);
        setHazardConfigOptions([]);
        setContraflowConfigOptions([]);
        setBlockedRoadConfigOptions([]);
      }
    );

    return () => unsub();
  }, [customConfigColRef]);

  useEffect(() => {
    if (!hazardLayersColRef) {
      setHazardLayers([]);
      setHazardLayersLoaded(true);
      return undefined;
    }
    setHazardLayersLoaded(false);
    const unsub = onSnapshot(
      hazardLayersColRef,
      (snap) => {
        setHazardLayers(snap.docs
          .map((item) => ({ id: item.id, ...(item.data() || {}) }))
          .sort((a, b) => String(a.name || a.id).localeCompare(String(b.name || b.id))));
        setHazardLayersLoaded(true);
      },
      (err) => {
        console.warn("Failed to load hazard layer options:", err);
        setHazardLayers([]);
        setHazardLayersLoaded(true);
      }
    );
    return () => unsub();
  }, [hazardLayersColRef]);

  const layerOptionsByType = useMemo(() => {
    const out = { links: [], nodes: [], parcels: [] };
    (gisLayers || []).forEach((row) => {
      if (!RUN_LAYER_TYPES.includes(row.type)) return;
      out[row.type].push(row);
    });
    return out;
  }, [gisLayers]);

  const resolveLayerDoc = useCallback(
    (type, layerId = "", fileName = "") => {
      const targetType = String(type || "").toLowerCase();
      const options = layerOptionsByType[targetType] || [];
      if (!options.length) return null;

      const wantedId = String(layerId || "").trim();
      if (wantedId) {
        const byId = options.find((row) => String(row.id) === wantedId);
        if (byId) return byId;
      }

      const wantedFile = String(fileName || "").trim().toLowerCase();
      if (wantedFile) {
        const byFile = options.find(
          (row) => String(row.filename || "").trim().toLowerCase() === wantedFile
        );
        if (byFile) return byFile;
      }

      const defaultFile = String(LAYER_FILE[targetType] || "").trim().toLowerCase();
      const byDefault = options.find(
        (row) => String(row.filename || "").trim().toLowerCase() === defaultFile
      );
      return byDefault || options[0];
    },
    [layerOptionsByType]
  );

  const buildRunLayersPayload = useCallback(
    (run, draft = {}) => {
      const payload = {};

      RUN_LAYER_TYPES.forEach((type) => {
        const layerIdKey = `${type}LayerId`;
        const fileNameKey = `${type}FileName`;
        const row = resolveLayerDoc(
          type,
          draft?.[layerIdKey] ?? run?.runLayers?.[layerIdKey] ?? "",
          draft?.[fileNameKey] ??
            run?.runLayers?.[fileNameKey] ??
            run?.runLayers?.[type] ??
            ""
        );
        payload[layerIdKey] = String(row?.id || "");
        payload[fileNameKey] = String(row?.filename || LAYER_FILE[type]);
      });

      const rawDestination =
        draft?.destinationConfigName ??
        run?.runLayers?.destinationConfigName ??
        DEFAULT_DESTINATION_CONFIG;
      const wantedDestination = String(rawDestination || "").trim();
      payload.destinationConfigName =
        destinationConfigOptions.includes(wantedDestination) && wantedDestination
          ? wantedDestination
          : DEFAULT_DESTINATION_CONFIG;

      const rawDepartureTimes =
        draft?.departureTimesConfigName ??
        run?.runLayers?.departureTimesConfigName ??
        run?.runLayers?.departure_times_config ??
        run?.runLayers?.departure_times ??
        DEFAULT_DEPARTURE_TIMES_CONFIG;
      const wantedDepartureTimes = String(rawDepartureTimes || "").trim();
      payload.departureTimesConfigName =
        departureTimesConfigOptions.includes(wantedDepartureTimes) && wantedDepartureTimes
          ? wantedDepartureTimes
          : DEFAULT_DEPARTURE_TIMES_CONFIG;

      const rawCriticalFacilities =
        draft?.criticalFacilities ??
        run?.runLayers?.criticalFacilities ??
        run?.runLayers?.critical_facilities ??
        DEFAULT_CRITICAL_FACILITIES;
      const wantedCriticalFacilities = String(rawCriticalFacilities || "").toLowerCase().trim();
      payload.criticalFacilities = CRITICAL_FACILITIES_OPTIONS.includes(wantedCriticalFacilities)
        ? wantedCriticalFacilities
        : DEFAULT_CRITICAL_FACILITIES;

      const rawHazardId = String(
        draft?.hazardLayerId ??
        run?.runLayers?.hazardLayerId ??
        run?.runLayers?.hazardLayerIds?.[0] ??
        ""
      );
      const availableHazards = new Map(hazardLayers.map((row) => [String(row.id), row]));
      payload.hazardLayerId = hazardLayersLoaded && !availableHazards.has(rawHazardId)
        ? ""
        : rawHazardId;
      payload.hazardLayerName = String(availableHazards.get(payload.hazardLayerId)?.name || "");

      const rawHazardConfig = String(
        draft?.hazardConfigName ??
        run?.runLayers?.hazardConfigName ??
        run?.runLayers?.hazard_config ??
        ""
      ).trim();
      payload.hazardConfigName = hazardConfigOptions.includes(rawHazardConfig)
        ? rawHazardConfig
        : "";

      const rawContraflowConfig = String(
        draft?.contraflowConfigName ??
        run?.runLayers?.contraflowConfigName ??
        run?.runLayers?.contraflow_config ??
        ""
      ).trim();
      const compatibleContraflow = contraflowConfigOptions.find((option) =>
        option.name === rawContraflowConfig &&
        String(option.linksLayerId) === String(payload.linksLayerId)
      );
      payload.contraflowConfigName = compatibleContraflow?.name || "";
      const rawBlockedRoadConfig = String(draft?.blockedRoadConfigName ?? run?.runLayers?.blockedRoadConfigName ?? run?.runLayers?.blocked_road_config ?? "").trim();
      const compatibleBlockedRoad = blockedRoadConfigOptions.find((option) => option.name === rawBlockedRoadConfig && String(option.linksLayerId) === String(payload.linksLayerId));
      payload.blockedRoadConfigName = compatibleBlockedRoad?.name || "";

      return payload;
    },
    [resolveLayerDoc, destinationConfigOptions, departureTimesConfigOptions, hazardConfigOptions, contraflowConfigOptions, blockedRoadConfigOptions, hazardLayers, hazardLayersLoaded]
  );

  useEffect(() => {
    setFieldDrafts((prev) => {
      const next = { ...prev };
      runs.forEach((run) => {
        const existing = prev[run.id] || {};
        const layerPayload = buildRunLayersPayload(run, existing);
        next[run.id] = {
          vehiclesPerHousehold:
            existing.vehiclesPerHousehold != null
              ? existing.vehiclesPerHousehold
              : String(run.vehiclesPerHousehold),
          randomSeed: existing.randomSeed != null ? existing.randomSeed : String(run.randomSeed),
          routingStrategy: existing.routingStrategy || run.routingStrategy,
          linksLayerId:
            existing.linksLayerId != null ? existing.linksLayerId : String(layerPayload.linksLayerId || ""),
          nodesLayerId:
            existing.nodesLayerId != null ? existing.nodesLayerId : String(layerPayload.nodesLayerId || ""),
          parcelsLayerId:
            existing.parcelsLayerId != null ? existing.parcelsLayerId : String(layerPayload.parcelsLayerId || ""),
          linksFileName:
            existing.linksFileName != null
              ? existing.linksFileName
              : String(layerPayload.linksFileName || LAYER_FILE.links),
          nodesFileName:
            existing.nodesFileName != null
              ? existing.nodesFileName
              : String(layerPayload.nodesFileName || LAYER_FILE.nodes),
          parcelsFileName:
            existing.parcelsFileName != null
              ? existing.parcelsFileName
              : String(layerPayload.parcelsFileName || LAYER_FILE.parcels),
          destinationConfigName:
            existing.destinationConfigName != null
              ? existing.destinationConfigName
              : String(layerPayload.destinationConfigName || DEFAULT_DESTINATION_CONFIG),
          departureTimesConfigName:
            existing.departureTimesConfigName != null
              ? existing.departureTimesConfigName
              : String(layerPayload.departureTimesConfigName || DEFAULT_DEPARTURE_TIMES_CONFIG),
          criticalFacilities:
            existing.criticalFacilities != null
              ? existing.criticalFacilities
              : String(layerPayload.criticalFacilities || DEFAULT_CRITICAL_FACILITIES),
          hazardLayerId: String(layerPayload.hazardLayerId || ""),
          hazardConfigName: String(layerPayload.hazardConfigName || ""),
          blockedRoadConfigName:
            existing.blockedRoadConfigName != null
              ? existing.blockedRoadConfigName
              : String(run?.runLayers?.blockedRoadConfigName || layerPayload.blockedRoadConfigName || ""),
          contraflowConfigName:
            existing.contraflowConfigName != null
              ? existing.contraflowConfigName
              : String(run?.runLayers?.contraflowConfigName || layerPayload.contraflowConfigName || ""),
        };
      });

      const validIds = new Set(runs.map((r) => r.id));
      Object.keys(next).forEach((id) => {
        if (!validIds.has(id)) delete next[id];
      });

      return next;
    });
  }, [runs, buildRunLayersPayload]);

  const totalPages = Math.max(1, Math.ceil(runs.length / PAGE_SIZE));

  useEffect(() => {
    setPage((prev) => Math.min(totalPages, Math.max(1, prev)));
  }, [totalPages]);

  const pagedRuns = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return runs.slice(start, start + PAGE_SIZE);
  }, [runs, page]);

  const downloadRunsExcel = useCallback(() => {
    const rows = (runs || []).map((run) => {
      const draft = fieldDrafts[run.id] || {};
      const runLayers = buildRunLayersPayload(run, draft);
      const linksLayer = resolveLayerDoc(
        "links",
        runLayers.linksLayerId,
        runLayers.linksFileName
      );
      const nodesLayer = resolveLayerDoc(
        "nodes",
        runLayers.nodesLayerId,
        runLayers.nodesFileName
      );
      const parcelsLayer = resolveLayerDoc(
        "parcels",
        runLayers.parcelsLayerId,
        runLayers.parcelsFileName
      );
      const routing = ROUTING_OPTIONS.includes(String(draft.routingStrategy || "").toLowerCase())
        ? String(draft.routingStrategy).toLowerCase()
        : String(run.routingStrategy || "k1").toLowerCase();

      return {
        run_id: String(run.id || ""),
        name: String(run.name || ""),
        status: String(run.status || ""),
        vehicles_per_household: toNumberOrFallback(
          draft.vehiclesPerHousehold,
          run.vehiclesPerHousehold
        ),
        routing_strategy: routing,
        random_seed: toNumberOrFallback(draft.randomSeed, run.randomSeed),
        links_layer_name: String(linksLayer?.name || ""),
        links_file_name: String(runLayers.linksFileName || linksLayer?.filename || LAYER_FILE.links),
        nodes_layer_name: String(nodesLayer?.name || ""),
        nodes_file_name: String(runLayers.nodesFileName || nodesLayer?.filename || LAYER_FILE.nodes),
        parcels_layer_name: String(parcelsLayer?.name || ""),
        parcels_file_name: String(
          runLayers.parcelsFileName || parcelsLayer?.filename || LAYER_FILE.parcels
        ),
        destination_config_name: String(
          runLayers.destinationConfigName || DEFAULT_DESTINATION_CONFIG
        ),
        departure_times_config_name: String(
          runLayers.departureTimesConfigName || DEFAULT_DEPARTURE_TIMES_CONFIG
        ),
        critical_facilities: String(
          runLayers.criticalFacilities || DEFAULT_CRITICAL_FACILITIES
        ),
        hazard_id: String(runLayers.hazardLayerId || ""),
        hazard_config: String(runLayers.hazardConfigName || ""),
        contraflow_config: String(runLayers.contraflowConfigName || ""),
        blocked_road_config: String(runLayers.blockedRoadConfigName || ""),
        created_at_ms: Number(run.createdAtMs || 0),
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "runs");

    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    const sim = String(simulationId || "simulation");
    XLSX.writeFile(workbook, `builder_runs_${sim}_${stamp}.xlsx`);
  }, [runs, fieldDrafts, buildRunLayersPayload, resolveLayerDoc, simulationId]);

  useEffect(() => {
    const runName = String(logsRun?.name || "").trim();
    if (!database || !runName) {
      setLogLines([]);
      return undefined;
    }

    const logsRef = dbRef(database, `model_runs/${runName}`);
    const unsub = onValue(logsRef, (snap) => {
      const val = snap.val();
      if (!val || typeof val !== "object") {
        setLogLines([]);
        return;
      }

      const lines = Object.entries(val)
        .map(([k, v]) => ({
          ts: Number.isFinite(Number(k)) ? Number(k) : 0,
          message: String(v ?? ""),
        }))
        .sort((a, b) => a.ts - b.ts);

      setLogLines(lines);
    }, (error) => trackBuilderFailure("builder_run_logs_load_failed", { builder_id: simulationId, tab: "run", run_id: logsRun?.id, endpoint: "run_logs" }, error));

    return () => unsub();
  }, [database, logsRun?.name]);

  const runDocRef = useCallback(
    (runId) => doc(firestore, "model_builders", String(simulationId), "runs", String(runId)),
    [simulationId]
  );

  const startEditingName = useCallback((run) => {
    setEditingRunId(run.id);
    setNameDraft(run.name);
  }, []);

  const cancelEditingName = useCallback(() => {
    setEditingRunId("");
    setNameDraft("");
  }, []);

  const saveName = useCallback(
    async (runId) => {
      const nextName = nameDraft.trim();
      if (!nextName) return;
      setSaveError("");
      setBusyRunId(runId);
      try {
        await updateDoc(runDocRef(runId), {
          name: nextName,
          updatedAt: serverTimestamp(),
        });
        setEditingRunId("");
        setNameDraft("");
      } catch (e) {
        setSaveError(String(e?.message || e));
      } finally {
        setBusyRunId("");
      }
    },
    [nameDraft, runDocRef]
  );

  const saveRowFields = useCallback(
    async (runId, overrideDraft = null) => {
      const draft = overrideDraft || fieldDrafts[runId];
      if (!draft) return;
      const run = runs.find((r) => r.id === runId) || null;

      const vehicles = toNumberOrFallback(draft.vehiclesPerHousehold, 1);
      const randomSeed = toNumberOrFallback(draft.randomSeed, 42);
      const routing = ROUTING_OPTIONS.includes(String(draft.routingStrategy).toLowerCase())
        ? String(draft.routingStrategy).toLowerCase()
        : "k1";
      const runLayers = buildRunLayersPayload(run, draft);

      setSaveError("");
      setBusyRunId(runId);
      try {
        await updateDoc(runDocRef(runId), {
          vehiclesPerHousehold: vehicles,
          randomSeed,
          routingStrategy: routing,
          runLayers,
          updatedAt: serverTimestamp(),
        });
        trackBuilderEvent("builder_run_draft_saved", { builder_id: simulationId, tab: "run", run_id: runId });
      } catch (e) {
        trackBuilderFailure("builder_run_save_failed", { builder_id: simulationId, tab: "run", run_id: runId, endpoint: "runs_document" }, e);
        setSaveError(String(e?.message || e));
      } finally {
        setBusyRunId("");
      }
    },
    [fieldDrafts, runs, runDocRef, buildRunLayersPayload]
  );

  const addRun = useCallback(async () => {
    if (!runsColRef) return;
    setSaveError("");
    setBusyRunId("__adding__");
    const createdAtMs = Date.now();
    const name = defaultRunName(runs.map((run) => run.name));
    const d = doc(runsColRef);
    try {
      const runLayers = buildRunLayersPayload(null, {});
      await setDoc(d, {
        name,
        vehiclesPerHousehold: 1,
        routingStrategy: "k1",
        randomSeed: 42,
        runLayers,
        status: "draft",
        createdAtMs,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      setEditingRunId(d.id);
      setNameDraft(name);
      setPage(1);
      trackBuilderEvent("builder_run_draft_created", { builder_id: simulationId, tab: "run", run_id: d.id });
    } catch (e) {
      trackBuilderFailure("builder_run_save_failed", { builder_id: simulationId, tab: "run", endpoint: "runs_document" }, e);
      setSaveError(String(e?.message || e));
    } finally {
      setBusyRunId("");
    }
  }, [runsColRef, buildRunLayersPayload, runs]);

  const confirmRunNow = useCallback(async () => {
    if (!confirmRun?.id || !simulationId) return;
    setSaveError("");
    setBusyRunId(confirmRun.id);
    try {
      const draft = fieldDrafts[confirmRun.id] || {};
      const vehiclesPerHousehold = toNumberOrFallback(draft.vehiclesPerHousehold, confirmRun.vehiclesPerHousehold);
      const randomSeed = toNumberOrFallback(draft.randomSeed, confirmRun.randomSeed);
      const routingUi =
        ROUTING_OPTIONS.includes(String(draft.routingStrategy || "").toLowerCase())
          ? String(draft.routingStrategy).toLowerCase()
          : String(confirmRun.routingStrategy || "k1").toLowerCase();
      const runLayers = buildRunLayersPayload(confirmRun, draft);

      await updateDoc(runDocRef(confirmRun.id), {
        vehiclesPerHousehold,
        randomSeed,
        routingStrategy: routingUi,
        runLayers,
        status: "running",
        runRequestedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const runNameForApi = String(confirmRun.name || "").trim();
      const res = await cloudRunStartGeneralModel(String(simulationId), runNameForApi, {
        runMetadataId: String(confirmRun.id || ""),
        resultFirestorePath:
          "model_builders/" + String(simulationId) + "/results/" + String(confirmRun.id),
        vehiclesPerHousehold,
        randomSeed,
        routingStrat: mapRoutingForApi(routingUi),
        linksFile: String(runLayers.linksFileName || LAYER_FILE.links),
        nodesFile: String(runLayers.nodesFileName || LAYER_FILE.nodes),
        parcelsFile: String(runLayers.parcelsFileName || LAYER_FILE.parcels),
        destinations: String(runLayers.destinationConfigName || DEFAULT_DESTINATION_CONFIG),
        departureTimes: String(
          runLayers.departureTimesConfigName || DEFAULT_DEPARTURE_TIMES_CONFIG
        ),
        criticalFacilities: String(
          runLayers.criticalFacilities || DEFAULT_CRITICAL_FACILITIES
        ),
        hazardId: runLayers.hazardLayerId || null,
        hazardConfig: runLayers.hazardConfigName || null,
        contraflowConfig: runLayers.contraflowConfigName || null,
        blockedRoadConfig: runLayers.blockedRoadConfigName || null,
      });

      if (!res?.ok) {
        await updateDoc(runDocRef(confirmRun.id), {
          status: "draft",
          lastError: String(res?.error || "Failed to trigger simulation run"),
          updatedAt: serverTimestamp(),
        });
        throw new Error(res?.error || "Failed to trigger simulation run");
      }
      trackBuilderEvent("builder_run_submitted", { builder_id: simulationId, tab: "run", run_id: confirmRun.id });
      setConfirmRun(null);
    } catch (e) {
      trackBuilderFailure("builder_run_submit_failed", { builder_id: simulationId, tab: "run", run_id: confirmRun.id, endpoint: "start_general_model" }, e);
      setSaveError(String(e?.message || e));
    } finally {
      setBusyRunId("");
    }
  }, [confirmRun, fieldDrafts, runDocRef, simulationId, buildRunLayersPayload]);

  const deleteRun = useCallback(
    async (run) => {
      if (!run?.id || run.status !== "draft" || !simulationId) return;
      const shouldDelete = window.confirm(`Delete "${run.name}"? This cannot be undone.`);
      if (!shouldDelete) return;

      setSaveError("");
      setBusyRunId(run.id);
      try {
        await deleteDoc(runDocRef(run.id));
        trackBuilderEvent("builder_run_deleted", { builder_id: simulationId, tab: "run", run_id: run.id });
      } catch (e) {
        trackBuilderFailure("builder_run_delete_failed", { builder_id: simulationId, tab: "run", run_id: run.id, endpoint: "runs_document" }, e);
        setSaveError(String(e?.message || e));
      } finally {
        setBusyRunId("");
      }
    },
    [runDocRef, simulationId]
  );

  const changeRunLayer = useCallback(
    (run, type, rawLayerId) => {
      if (!run?.id || !RUN_LAYER_TYPES.includes(type)) return;
      const picked = resolveLayerDoc(type, rawLayerId, "");
      const nextDraft = {
        ...(fieldDrafts[run.id] || {}),
        [`${type}LayerId`]: String(picked?.id || ""),
        [`${type}FileName`]: String(picked?.filename || LAYER_FILE[type]),
        ...(type === "links" ? { contraflowConfigName: "", blockedRoadConfigName: "" } : {}),
      };
      setFieldDrafts((prev) => ({
        ...prev,
        [run.id]: nextDraft,
      }));
      saveRowFields(run.id, nextDraft);
    },
    [fieldDrafts, resolveLayerDoc, saveRowFields]
  );

  const changeRunHazard = useCallback((run, hazardLayerId) => {
    if (!run?.id) return;
    const nextDraft = {
      ...(fieldDrafts[run.id] || {}),
      hazardLayerId: String(hazardLayerId || ""),
    };
    setFieldDrafts((prev) => ({ ...prev, [run.id]: nextDraft }));
    saveRowFields(run.id, nextDraft);
  }, [fieldDrafts, saveRowFields]);

  const changeRunHazardConfig = useCallback((run, hazardConfigName) => {
    if (!run?.id) return;
    const nextDraft = {
      ...(fieldDrafts[run.id] || {}),
      hazardConfigName: String(hazardConfigName || ""),
    };
    setFieldDrafts((prev) => ({ ...prev, [run.id]: nextDraft }));
    saveRowFields(run.id, nextDraft);
  }, [fieldDrafts, saveRowFields]);

  const changeRunContraflowConfig = useCallback((run, contraflowConfigName) => {
    if (!run?.id) return;
    const nextDraft = {
      ...(fieldDrafts[run.id] || {}),
      contraflowConfigName: String(contraflowConfigName || ""),
    };
    setFieldDrafts((prev) => ({ ...prev, [run.id]: nextDraft }));
    saveRowFields(run.id, nextDraft);
  }, [fieldDrafts, saveRowFields]);

  const changeRunBlockedRoadConfig = useCallback((run, blockedRoadConfigName) => {
    if (!run?.id) return;
    const nextDraft = { ...(fieldDrafts[run.id] || {}), blockedRoadConfigName: String(blockedRoadConfigName || "") };
    setFieldDrafts((prev) => ({ ...prev, [run.id]: nextDraft }));
    saveRowFields(run.id, nextDraft);
  }, [fieldDrafts, saveRowFields]);

  const confirmRunLayers = useMemo(() => {
    if (!confirmRun?.id) return null;
    return buildRunLayersPayload(confirmRun, fieldDrafts[confirmRun.id] || {});
  }, [confirmRun, fieldDrafts, buildRunLayersPayload]);

  const linesCount = logLines.length;

  return (
    <div className="mb-run-shell">
      <div className="mb-run-head">
        <div className="mb-run-title">Simulation Runs</div>
        <div className="mb-run-actions">
          <button
            className="mb-run-btnSecondary"
            onClick={downloadRunsExcel}
            disabled={!runs.length}
          >
            Download
          </button>
          <button
            className="mb-run-cta"
            onClick={addRun}
            disabled={!simulationId || busyRunId === "__adding__"}
          >
            {busyRunId === "__adding__" ? "Adding..." : "Add Run"}
          </button>
        </div>
      </div>
      {saveError ? <div className="mb-run-error">{saveError}</div> : null}
      {!simulationId ? (
        <div className="mb-run-error">
          Missing simulation id. Expected Firestore path: model_builders/{`{simulationId}`}/runs
        </div>
      ) : null}

      <div className="mb-run-tableWrap">
        <table className="mb-run-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Links</th>
              <th>Nodes</th>
              <th>Parcels</th>
              <th>Destination</th>
              <th>Departure Times</th>
              <th>Critical Facilities</th>
              {showHazards && <th>Hazard Layer</th>}
              {showHazards && <th>Hazard Config</th>}
              <th>Contraflow Config</th>
              <th>Blocked Road Config</th>
              <th>Veh/HH</th>
              <th>Routing Strategy</th>
              <th>Random Seed</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={showHazards ? 16 : 14} className="mb-run-empty">Loading runs...</td>
              </tr>
            ) : pagedRuns.length ? (
              pagedRuns.map((run) => {
                const isEditing = editingRunId === run.id;
                const isBusy = busyRunId === run.id;
                const showRunCta = run.status === "draft";
                const draft = fieldDrafts[run.id] || {};
                const runLayers = buildRunLayersPayload(run, draft);
                return (
                  <tr key={run.id}>
                    <td>
                      {isEditing ? (
                        <div className="mb-run-nameEdit">
                          <input
                            className="mb-run-nameInput"
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            autoFocus
                          />
                          <button className="mb-run-iconBtn" onClick={() => saveName(run.id)} disabled={isBusy}>
                            ✓
                          </button>
                          <button className="mb-run-iconBtn isMuted" onClick={cancelEditingName} disabled={isBusy}>
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="mb-run-nameView">
                          <span>{run.name}</span>
                          <button
                            className="mb-run-iconBtn isMuted"
                            onClick={() => startEditingName(run)}
                            disabled={run.status !== "draft" || isBusy}
                          >
                            ✎
                          </button>
                        </div>
                      )}
                    </td>
                    <td>
                      <select
                        className="mb-run-cellSelect"
                        value={String(runLayers.linksLayerId || "")}
                        onChange={(e) => changeRunLayer(run, "links", e.target.value)}
                        disabled={isBusy || run.status !== "draft" || !layerOptionsByType.links.length}
                      >
                        {!layerOptionsByType.links.length ? (
                          <option value="">No link layers</option>
                        ) : (
                          layerOptionsByType.links.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name} ({opt.filename})
                            </option>
                          ))
                        )}
                      </select>
                    </td>
                    <td>
                      <select
                        className="mb-run-cellSelect"
                        value={String(runLayers.nodesLayerId || "")}
                        onChange={(e) => changeRunLayer(run, "nodes", e.target.value)}
                        disabled={isBusy || run.status !== "draft" || !layerOptionsByType.nodes.length}
                      >
                        {!layerOptionsByType.nodes.length ? (
                          <option value="">No node layers</option>
                        ) : (
                          layerOptionsByType.nodes.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name} ({opt.filename})
                            </option>
                          ))
                        )}
                      </select>
                    </td>
                    <td>
                      <select
                        className="mb-run-cellSelect"
                        value={String(runLayers.parcelsLayerId || "")}
                        onChange={(e) => changeRunLayer(run, "parcels", e.target.value)}
                        disabled={isBusy || run.status !== "draft" || !layerOptionsByType.parcels.length}
                      >
                        {!layerOptionsByType.parcels.length ? (
                          <option value="">No parcel layers</option>
                        ) : (
                          layerOptionsByType.parcels.map((opt) => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name} ({opt.filename})
                            </option>
                          ))
                        )}
                      </select>
                    </td>
                    <td>
                      <select
                        className="mb-run-cellSelect"
                        value={String(runLayers.destinationConfigName || DEFAULT_DESTINATION_CONFIG)}
                        onChange={(e) => {
                          const value = String(e.target.value || DEFAULT_DESTINATION_CONFIG);
                          const nextDraft = {
                            ...(fieldDrafts[run.id] || {}),
                            destinationConfigName: value,
                          };
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [run.id]: nextDraft,
                          }));
                          saveRowFields(run.id, nextDraft);
                        }}
                        disabled={isBusy || run.status !== "draft"}
                      >
                        {destinationConfigOptions.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="mb-run-cellSelect"
                        value={String(
                          runLayers.departureTimesConfigName || DEFAULT_DEPARTURE_TIMES_CONFIG
                        )}
                        onChange={(e) => {
                          const value = String(e.target.value || DEFAULT_DEPARTURE_TIMES_CONFIG);
                          const nextDraft = {
                            ...(fieldDrafts[run.id] || {}),
                            departureTimesConfigName: value,
                          };
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [run.id]: nextDraft,
                          }));
                          saveRowFields(run.id, nextDraft);
                        }}
                        disabled={isBusy || run.status !== "draft"}
                      >
                        {departureTimesConfigOptions.map((name) => (
                          <option key={name} value={name}>
                            {name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <select
                        className="mb-run-cellSelect"
                        value={String(runLayers.criticalFacilities || DEFAULT_CRITICAL_FACILITIES)}
                        onChange={(e) => {
                          const value = String(
                            e.target.value || DEFAULT_CRITICAL_FACILITIES
                          ).toLowerCase();
                          const nextDraft = {
                            ...(fieldDrafts[run.id] || {}),
                            criticalFacilities: value,
                          };
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [run.id]: nextDraft,
                          }));
                          saveRowFields(run.id, nextDraft);
                        }}
                        disabled={isBusy || run.status !== "draft"}
                      >
                        {CRITICAL_FACILITIES_OPTIONS.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </td>
                    {showHazards && <td>
                        <HazardSelect
                          options={hazardLayers}
                          selectedId={runLayers.hazardLayerId || ""}
                          disabled={isBusy || run.status !== "draft" || !hazardLayersLoaded}
                          onChange={(id) => changeRunHazard(run, id)}
                      />
                    </td>}
                    {showHazards && <td>
                      <select
                        className="mb-run-cellSelect"
                        value={String(runLayers.hazardConfigName || "")}
                        disabled={isBusy || run.status !== "draft"}
                        onChange={(e) => changeRunHazardConfig(run, e.target.value)}
                      >
                        <option value="">(None)</option>
                        {hazardConfigOptions.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </td>}
                    <td>
                      <select
                        className="mb-run-cellSelect"
                        value={String(runLayers.contraflowConfigName || "")}
                        disabled={isBusy || run.status !== "draft"}
                        onChange={(e) => changeRunContraflowConfig(run, e.target.value)}
                      >
                        <option value="">(None)</option>
                        {contraflowConfigOptions
                          .filter((option) => String(option.linksLayerId) === String(runLayers.linksLayerId))
                          .map((option) => (
                            <option key={option.name} value={option.name}>{option.name}</option>
                          ))}
                      </select>
                    </td>
                    <td>
                      <select className="mb-run-cellSelect" value={String(runLayers.blockedRoadConfigName || "")}
                        disabled={isBusy || run.status !== "draft"}
                        onChange={(event) => changeRunBlockedRoadConfig(run, event.target.value)}>
                        <option value="">(None)</option>
                        {blockedRoadConfigOptions.filter((option) => String(option.linksLayerId) === String(runLayers.linksLayerId))
                          .map((option) => <option key={option.name} value={option.name}>{option.name}</option>)}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="mb-run-cellInput"
                        value={draft.vehiclesPerHousehold ?? ""}
                        onChange={(e) =>
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [run.id]: { ...(prev[run.id] || {}), vehiclesPerHousehold: e.target.value },
                          }))
                        }
                        onBlur={() => saveRowFields(run.id)}
                        disabled={isBusy || run.status !== "draft"}
                      />
                    </td>
                    <td>
                      <select
                        className="mb-run-cellSelect"
                        value={draft.routingStrategy || "k1"}
                        onChange={(e) => {
                          const value = e.target.value;
                          const nextDraft = { ...(fieldDrafts[run.id] || {}), routingStrategy: value };
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [run.id]: nextDraft,
                          }));
                          saveRowFields(run.id, nextDraft);
                        }}
                        disabled={isBusy || run.status !== "draft"}
                      >
                        {ROUTING_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                      </select>
                    </td>
                    <td>
                      <input
                        type="number"
                        className="mb-run-cellInput"
                        value={draft.randomSeed ?? ""}
                        onChange={(e) =>
                          setFieldDrafts((prev) => ({
                            ...prev,
                            [run.id]: { ...(prev[run.id] || {}), randomSeed: e.target.value },
                          }))
                        }
                        onBlur={() => saveRowFields(run.id)}
                        disabled={isBusy || run.status !== "draft"}
                      />
                    </td>
                    <td>
                      <span className={`mb-run-status is-${run.status}`}>
                        {formatStatus(run.status)}
                      </span>
                    </td>
                    <td>
                      {showRunCta ? (
                        <div className="mb-run-actions">
                          <button
                            className="mb-run-cta"
                            onClick={() => setConfirmRun(run)}
                            disabled={isBusy}
                          >
                            {isBusy ? "Running..." : "Run"}
                          </button>
                          <button
                            className="mb-run-delete"
                            onClick={() => deleteRun(run)}
                            disabled={isBusy}
                          >
                            Delete
                          </button>
                        </div>
                      ) : (
                        <button
                          className="mb-run-view"
                          onClick={() => { setLogsRun(run); trackBuilderEvent("builder_run_logs_opened", { builder_id: simulationId, tab: "run", run_id: run.id }); }}
                        >
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={showHazards ? 16 : 14} className="mb-run-empty">
                  No runs found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {runs.length > PAGE_SIZE ? (
        <div className="mb-run-pagination">
          <button
            className="mb-run-btnSecondary"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            Prev
          </button>
          <span className="mb-run-pageText">Page {page} / {totalPages}</span>
          <button
            className="mb-run-btnSecondary"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
          >
            Next
          </button>
        </div>
      ) : null}

      {confirmRun ? (
        <div className="mb-run-modalBackdrop">
          <div className="mb-run-modal">
            <div className="mb-run-modalTitle">Confirm Simulation</div>
            <div className="mb-run-modalText">
              Run "{confirmRun.name}" with {toNumberOrFallback(fieldDrafts[confirmRun.id]?.vehiclesPerHousehold, confirmRun.vehiclesPerHousehold)} veh/HH and {String(fieldDrafts[confirmRun.id]?.routingStrategy || confirmRun.routingStrategy)} routing?
            </div>
            <div className="mb-run-modalText">
              Inputs: {confirmRunLayers?.linksFileName || LAYER_FILE.links}, {confirmRunLayers?.nodesFileName || LAYER_FILE.nodes}, {confirmRunLayers?.parcelsFileName || LAYER_FILE.parcels}
            </div>
            <div className="mb-run-modalText">
              Destination config: {confirmRunLayers?.destinationConfigName || DEFAULT_DESTINATION_CONFIG}
            </div>
            <div className="mb-run-modalText">
              Departure times: {confirmRunLayers?.departureTimesConfigName || DEFAULT_DEPARTURE_TIMES_CONFIG}
            </div>
            <div className="mb-run-modalText">
              Critical facilities: {confirmRunLayers?.criticalFacilities || DEFAULT_CRITICAL_FACILITIES}
            </div>
            {showHazards && <div className="mb-run-modalText">
              Hazard layer: {confirmRunLayers?.hazardLayerName || "(None)"}
            </div>}
            {showHazards && <div>
              Hazard config: {confirmRunLayers?.hazardConfigName || "(None)"}
            </div>}
            <div className="mb-run-modalText">
              Contraflow config: {confirmRunLayers?.contraflowConfigName || "(None)"}
            </div>
            <div className="mb-run-modalText">Blocked road config: {confirmRunLayers?.blockedRoadConfigName || "(None)"}</div>
            <div className="mb-run-modalActions">
              <button className="mb-run-btnSecondary" onClick={() => setConfirmRun(null)} disabled={busyRunId === confirmRun.id}>
                Cancel
              </button>
              <button className="mb-run-cta" onClick={confirmRunNow} disabled={busyRunId === confirmRun.id}>
                {busyRunId === confirmRun.id ? "Starting..." : "Run Simulation"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {logsRun ? (
        <div className="mb-run-modalBackdrop">
          <div className="mb-run-modal isWide">
            <div className="mb-run-logHead">
              <div className="mb-run-logTitle">{logsRun.name}</div>
              <div className="mb-run-logMeta">{linesCount} lines</div>
            </div>
            <div className="mb-run-logSubhead">
              <span className="mb-run-kv">Vehicles/HH: {logsRun.vehiclesPerHousehold}</span>
              <span className="mb-run-kv">Strategy: {logsRun.routingStrategy}</span>
              <span className="mb-run-kv">Random Seed: {logsRun.randomSeed}</span>
              <span className="mb-run-kv">Links: {logsRun?.runLayers?.linksFileName || LAYER_FILE.links}</span>
              <span className="mb-run-kv">Nodes: {logsRun?.runLayers?.nodesFileName || LAYER_FILE.nodes}</span>
              <span className="mb-run-kv">Parcels: {logsRun?.runLayers?.parcelsFileName || LAYER_FILE.parcels}</span>
              <span className="mb-run-kv">Destination: {logsRun?.runLayers?.destinationConfigName || DEFAULT_DESTINATION_CONFIG}</span>
              <span className="mb-run-kv">Departure Times: {logsRun?.runLayers?.departureTimesConfigName || DEFAULT_DEPARTURE_TIMES_CONFIG}</span>
              <span className="mb-run-kv">Critical Facilities: {logsRun?.runLayers?.criticalFacilities || DEFAULT_CRITICAL_FACILITIES}</span>
              {showHazards && <span className="mb-run-kv">Hazard Layer: {logsRun?.runLayers?.hazardLayerName || "(None)"}</span>}
              {showHazards && <span className="mb-run-kv">Hazard Config: {logsRun?.runLayers?.hazardConfigName || "(None)"}</span>}
              <span className="mb-run-kv">Contraflow Config: {logsRun?.runLayers?.contraflowConfigName || "(None)"}</span>
              <span className="mb-run-kv">Blocked Road Config: {logsRun?.runLayers?.blockedRoadConfigName || "(None)"}</span>
            </div>
            <div className="mb-run-logBox">
              {logLines.length ? (
                logLines.map((line, idx) => (
                  <div key={`${line.ts}-${idx}`} className="mb-run-logLine">
                    <span className="mb-run-logTime">
                      [{line.ts ? new Date(line.ts).toLocaleTimeString() : "--:--:--"}]
                    </span>
                    <span className="mb-run-logMsg">{line.message}</span>
                  </div>
                ))
              ) : (
                <div className="mb-run-empty">No logs yet for this run.</div>
              )}
            </div>
            <div className="mb-run-modalActions">
              <button className="mb-run-btnSecondary" onClick={() => setLogsRun(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
